import { FilterField, labelId, ParsedFilter, parseToken } from "./advancedFilters";


export interface NormalizeOptions {
  /** If true, unquoted string chips default to CONTAINS (:) */
  defaultStringToContains?: boolean; // default true
  /** If true, quoted strings enforce EXACT (=) and quotes are stripped */
  quotedStringsExact?: boolean;      // default true
}

export function makeKeyMaps<T>(fields: FilterField<T>[]) {
  const byId = new Map<string, FilterField<T>>();
  const keyMap = new Map<string, string>(); // label/id → canonical id

  for (const f of fields) {
    const id = (f.key ?? labelId(f.label)).toLowerCase();
    const label = f.label.toLowerCase();
    byId.set(id, f);
    keyMap.set(id, id);     // "portid" → "portid"
    keyMap.set(label, id);  // "port id" → "portid"
  }
  return { byId, keyMap };
}

export function normalizeFilters<T>(
  activeFilters: ParsedFilter[],
  fields: FilterField<T>[],
  opts: NormalizeOptions = {}
): ParsedFilter[] {
  const defaultStringToContains = opts.defaultStringToContains ?? true;
  const quotedStringsExact = opts.quotedStringsExact ?? true;

  const { byId, keyMap } = makeKeyMaps(fields);

  return activeFilters.map((f) => {
    // live (has op) → pass through; committed → parse
    const token: ParsedFilter = f.op
      ? { ...f }
      : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

    // map key to canonical id
    if (token.key) {
      const k = token.key.trim().toLowerCase();
      token.key = keyMap.get(k) ?? labelId(token.key);
    }

    // FREE-TEXT: always contains
    if (!token.key) {
      token.op = ":"; 
      return token;
    }

    // KEYED STRINGS: default to contains (:) unless quoted
    const field = byId.get(token.key);
    if (field?.type === "string") {
      const quoted = quotedStringsExact ? token.value.match(/^"(.*)"$/) : null;
      if (quoted) {
        token.value = quoted[1];    // strip quotes
        token.op = "=";             // exact match explicitly
      } else if (defaultStringToContains) {
        // Coerce '=' (or undefined) to ':' so partials like '13-a' match
        if (token.op === undefined || token.op === "=") {
          token.op = ":"; 
        }
      }
    }

    return token;
  });
}